/**
 * Construction Order API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  ConstructionOrder,
  ConstructionOrderDetail,
  ConstructionExtra,
  ConstructionStateOption,
  ConstructionOrderCreateRequest,
  ConstructionOrderUpdateRequest,
  ConstructionPhoto,
  Employee,
} from '../../types';

const API_PREFIX = '/api/saiens/construction';

interface OrderListParams {
  offset?: number;
  limit?: number;
  state?: string;
  sale_order_id?: number;
  search?: string;
  my_orders?: boolean;
}

/**
 * Get construction order list
 */
export async function getConstructionOrders(
  params: OrderListParams = {}
): Promise<ApiResponse<PaginatedData<ConstructionOrder>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<ConstructionOrder>>>(
    `${API_PREFIX}/orders`,
    { params }
  );
  return response.data;
}

/**
 * Get construction order detail
 */
export async function getConstructionOrder(orderId: number): Promise<ApiResponse<ConstructionOrderDetail>> {
  const response = await apiClient.get<ApiResponse<ConstructionOrderDetail>>(
    `${API_PREFIX}/orders/${orderId}`
  );
  return response.data;
}

/**
 * Create construction order
 */
export async function createOrder(
  data: ConstructionOrderCreateRequest
): Promise<ApiResponse<ConstructionOrderDetail>> {
  const response = await apiClient.post<ApiResponse<ConstructionOrderDetail>>(
    `${API_PREFIX}/orders`,
    data
  );
  return response.data;
}

/**
 * Update construction order
 */
export async function updateOrder(
  orderId: number,
  data: ConstructionOrderUpdateRequest
): Promise<ApiResponse<ConstructionOrderDetail>> {
  const response = await apiClient.put<ApiResponse<ConstructionOrderDetail>>(
    `${API_PREFIX}/orders/${orderId}`,
    data
  );
  return response.data;
}

/**
 * Get construction extra items
 */
export async function getExtras(): Promise<ApiResponse<ConstructionExtra[]>> {
  const response = await apiClient.get<ApiResponse<ConstructionExtra[]>>(
    `${API_PREFIX}/extras`
  );
  return response.data;
}

/**
 * Get construction states
 */
export async function getStates(): Promise<ApiResponse<ConstructionStateOption[]>> {
  const response = await apiClient.get<ApiResponse<ConstructionStateOption[]>>(
    `${API_PREFIX}/states`
  );
  return response.data;
}

/**
 * Get construction team members
 */
export async function getTeams(): Promise<ApiResponse<Employee[]>> {
  const response = await apiClient.get<ApiResponse<Employee[]>>(`${API_PREFIX}/teams`);
  return response.data;
}

/**
 * Get construction order photos
 */
export async function getPhotos(orderId: number): Promise<ApiResponse<ConstructionPhoto[]>> {
  const response = await apiClient.get<ApiResponse<ConstructionPhoto[]>>(
    `${API_PREFIX}/orders/${orderId}/photos`
  );
  return response.data;
}

/**
 * Upload construction order photos
 */
export async function uploadPhotos(
  orderId: number,
  images: string[]
): Promise<ApiResponse<ConstructionPhoto[]>> {
  const response = await apiClient.post<ApiResponse<ConstructionPhoto[]>>(
    `${API_PREFIX}/orders/${orderId}/photos`,
    { images }
  );
  return response.data;
}

/**
 * Delete construction order photo
 */
export async function deletePhoto(
  orderId: number,
  photoId: number
): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(
    `${API_PREFIX}/orders/${orderId}/photos/${photoId}`
  );
  return response.data;
}
